import { AxiosResponse } from "axios"
import { ICreateSocialGroup, IDistributeRequest, IDonateToProgramRequest, IDonateToUserRequest, IFoundation, IFoundationAccrualRequest, IInvitation, ISocialGroup, ISocialGroupAccrualRequest, ISocialProgram, ISocialProgramCategory, ISocialProgramGroup, ISocialProgramRegion, IUpdateSocialGroup, IUpdateSocialProgram, IWalletBalance } from "./types"
import { apiInstance } from "../base"
import { IBonusAccrualResponse } from "@/shared/types"

export const getFoundations = (): Promise<AxiosResponse<IFoundation[]>> => {
    return apiInstance.handler.get<IFoundation[]>(`/social-programs/foundations`)
}

export const getSocialPrograms = (type?: 'user' | 'foundation'): Promise<AxiosResponse<ISocialProgram[]>> => {
    return apiInstance.handler.get<ISocialProgram[]>(`/social-programs`, {
        params: { type }
    })
}

export const getSocialProgramById = (id: string): Promise<AxiosResponse<ISocialProgram>> => {
    return apiInstance.handler.get<ISocialProgram>(`/social-programs/${id}`)
}

export const createFoundationProgram = (data: FormData): Promise<AxiosResponse<ISocialProgram>> => {
    return apiInstance.handler.post<ISocialProgram>(`/social-programs/foundation`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
    })
}

export const deleteSocialProgram = (id: string): Promise<AxiosResponse<void>> => {
    return apiInstance.handler.delete(`/social-programs/${id}`)
}

export const getSocialProgramCategories = (): Promise<AxiosResponse<ISocialProgramCategory[]>> => {
    return apiInstance.handler.get<ISocialProgramCategory[]>(`/social-programs/categories`)
}

export const getSocialProgramRegions = (): Promise<AxiosResponse<ISocialProgramRegion[]>> => {
    return apiInstance.handler.get<ISocialProgramRegion[]>(`/social-programs/regions`)
}

export const removeSocialProgramMember = (programId: string, userId: string): Promise<AxiosResponse<void>> => {
    return apiInstance.handler.delete(`/social-programs/${programId}/members/${userId}`)
}

export const removeUserFromSocialGroup = (socialGroupId: string, userId: string): Promise<AxiosResponse<void>> => {
    return apiInstance.handler.post(`/social-groups/${socialGroupId}/remove/${userId}`)
}

export const blockUserProgramCreation = (userId: string): Promise<AxiosResponse<void>> => {
    return apiInstance.handler.post(`/social-programs/users/${userId}/block-creation`)
}

export const unblockUserProgramCreation = (userId: string): Promise<AxiosResponse<void>> => {
    return apiInstance.handler.post(`/social-programs/users/${userId}/unblock-creation`)
}

export const foundationAccrual = (programId: string, data: IFoundationAccrualRequest): Promise<AxiosResponse<IBonusAccrualResponse>> => {
    return apiInstance.handler.post(`/social-programs/${programId}/donate`, data)
}

// Groups
export const getSocialProgramGroups = (programId: string): Promise<AxiosResponse<ISocialProgramGroup[]>> => {
    return apiInstance.handler.get<ISocialProgramGroup[]>(`/social-programs/${programId}/groups`)
}

export const createSocialProgramGroup = (programId: string, name: string): Promise<AxiosResponse<ISocialProgramGroup>> => {
    return apiInstance.handler.post<ISocialProgramGroup>(`/social-programs/${programId}/groups`, { name })
}

export const deleteSocialProgramGroup = (programId: string, groupId: string): Promise<AxiosResponse<void>> => {
    return apiInstance.handler.delete(`/social-programs/${programId}/groups/${groupId}`)
}

export const addMemberToGroup = (programId: string, groupId: string, userId: string): Promise<AxiosResponse<void>> => {
    return apiInstance.handler.post(`/social-programs/${programId}/groups/${groupId}/members/${userId}`)
}

export const removeMemberFromGroup = (programId: string, groupId: string, userId: string): Promise<AxiosResponse<void>> => {
    return apiInstance.handler.delete(`/social-programs/${programId}/groups/${groupId}/members/${userId}`)
}

export const createGroupInvitation = (programId: string, groupId: string): Promise<AxiosResponse<IInvitation>> => {
    return apiInstance.handler.post<IInvitation>(`/social-programs/${programId}/groups/${groupId}/invite`)
}

export const getGroupInvitations = (programId: string, groupId: string): Promise<AxiosResponse<IInvitation[]>> => {
    return apiInstance.handler.get<IInvitation[]>(`/social-programs/${programId}/groups/${groupId}/invitations`)
}

export const deleteGroupInvitation = (programId: string, groupId: string, invitationId: string): Promise<AxiosResponse<void>> => {
    return apiInstance.handler.delete(`/social-programs/${programId}/groups/${groupId}/invitations/${invitationId}`)
}

export const getSocialProgramWallet = (programId: string): Promise<AxiosResponse<IWalletBalance>> => {
    return apiInstance.handler.get<IWalletBalance>(`/social-programs/${programId}/wallet`)
}

export const donateToUser = (programId: string, data: IDonateToUserRequest): Promise<AxiosResponse<IBonusAccrualResponse>> => {
    return apiInstance.handler.post<IBonusAccrualResponse>(`/social-programs/${programId}/donate/user`, data)
}

export const distributeToMembers = (programId: string, data: IDistributeRequest): Promise<AxiosResponse<number[]>> => {
    return apiInstance.handler.post<number[]>(`/social-programs/${programId}/donate/distribute`, data)
}

export const donateToProgram = (programId: string, targetProgramId: string, data: IDonateToProgramRequest): Promise<AxiosResponse<IBonusAccrualResponse>> => {
    return apiInstance.handler.post<IBonusAccrualResponse>(`/social-programs/${programId}/donate/program/${targetProgramId}`, data)
}

export const updateSocialProgram = (id: string, data: IUpdateSocialProgram | FormData): Promise<AxiosResponse<ISocialProgram>> => {
    const isFormData = data instanceof FormData
    return apiInstance.handler.put<ISocialProgram>(`/social-programs/${id}`, data, {
        headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
    })
}
